import axios, { AxiosRequestConfig } from 'axios';
import { useContext } from 'react';
import { TenantContext } from '../../context/tenant-context';
import { DateTime } from 'luxon';
import {
  OrderBufferPeriodLegacyRequest,
  OrderTotalsLegacyRequest,
  OrderTotalsLegacyResponse,
  ProductInfoLegacyRequest,
  ProductInfoLegacyResponse,
} from '../quipli-service/order/order-parsers';
import { CustomOrderPriceRequest } from '../quipli-service/pricing/pricing';

export const useYorentClient = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  function getOffRentForm(orderId: number): Promise<string> {
    const form = new FormData();
    form.append('order_id', `${orderId}`);
    form.append('mode', 'ajax');
    const config = {
      method: 'post',
      url: `${contextPath}manager/orders/offrent_form`,
      headers: {
        'X-Tenant': tenant,
        'Legacy-Route': true,
      },
      data: form,
    };
    return handleRequest(config);
  }

  function getOffRentWithInventoryForm(orderId: number): Promise<string> {
    const form = new FormData();
    form.append('order_id', `${orderId}`);
    form.append('mode', 'ajax');
    const config = {
      method: 'post',
      url: `${contextPath}manager/orders/off_rent_form`,
      headers: {
        'X-Tenant': tenant,
        'Legacy-Route': true,
      },
      data: form,
    };
    return handleRequest(config);
  }

  function getOnRentForm(orderId: number): Promise<string> {
    const form = new FormData();
    form.append('order_id', `${orderId}`);
    form.append('mode', 'ajax');
    const config = {
      method: 'post',
      url: `${contextPath}manager/orders/onrent_form`,
      headers: {
        'X-Tenant': tenant,
        'Legacy-Route': true,
      },
      data: form,
    };
    return handleRequest(config);
  }

  function getInventoryReturnForm(orderId: number): Promise<string> {
    const form = new FormData();
    form.append('order_id', `${orderId}`);
    form.append('mode', 'ajax');
    const config = {
      method: 'post',
      url: `${contextPath}manager/orders/inventoryreturn_form`,
      headers: {
        'X-Tenant': tenant,
        'Legacy-Route': true,
      },
      data: form,
    };
    return handleRequest(config);
  }

  async function resendCustomerReviewEmail(orderId: number): Promise<string> {
    const form = new FormData();
    form.append('order_id', `${orderId}`);

    const req = {
      method: 'POST',
      url: `${contextPath}manager/reviews/resendOrderReviewMail`,
      headers: {
        'X-Tenant': tenant,
        'Legacy-Route': true,
      },
      data: form,
    };

    return handleRequest<any>(req);
  }

  async function getNewProductPrice({
    productId,
    locationId,
    rentalStart,
    rentalEnd,
    isCycleBilled,
    cycleLength,
    orderId,
    customOrderRentalRates,
  }: {
    productId: number;
    locationId: number;
    rentalStart: DateTime;
    rentalEnd: DateTime;
    isCycleBilled?: boolean;
    cycleLength?: string;
    orderId?: number;
    customOrderRentalRates?: CustomOrderPriceRequest[];
  }): Promise<{ price: number; errors: string }> {
    const formData = new FormData();
    formData.append('prod_id', `${productId}`);
    formData.append('location_id', `${locationId}`);
    formData.append('start', rentalStart.toFormat('LL/dd/yyyy HH:mm'));
    formData.append('end', rentalEnd.toFormat('LL/dd/yyyy HH:mm'));
    formData.append('is_cycle_billed', isCycleBilled ? '1' : '0');
    formData.append('cycle_length', `${cycleLength}`);
    formData.append('order_id', `${orderId}`);
    formData.append('custom_order_rental_rates', JSON.stringify(customOrderRentalRates));

    const config = {
      method: 'post',
      url: `${contextPath}manager/orders/changeProductDates`,
      data: formData,
      responseType: 'text',
      transformResponse: (data) => {
        return JSON.parse(data);
      },
      headers: {
        'X-Tenant': tenant,
        'Legacy-Route': true,
        'Content-Type': 'multipart/form-data',
      },
    } as AxiosRequestConfig;

    return await handleRequest<{ price: number; errors: string }>(config);
  }

  async function getOrderTotalsLegacy(request: OrderTotalsLegacyRequest): Promise<OrderTotalsLegacyResponse> {
    const formData = new FormData();
    const { products, editable_additional_fees, ...props } = request;

    Object.entries(props).forEach(([key, value]) => {
      formData.append(key, value);
    });

    products.forEach((product) => {
      formData.append('new_product_id[]', product.new_product_id);
      formData.append('order_prod_id[]', product.order_prod_id);
      formData.append('new_product_start[]', product.new_product_start);
      formData.append('new_product_end[]', product.new_product_end);
      formData.append('new_product_type[]', product.new_product_type);
      formData.append('new_prod_unique_id[]', product.new_prod_unique_id);
      formData.append('new_product_delivery_method[]', product.new_product_delivery_method);
      formData.append('new_prod_editable_price[]', product.new_prod_editable_price);
      formData.append('new_prod_editable_disc[]', product.new_prod_editable_disc);
      formData.append('new_product_qty[]', product.new_product_qty);
      product.new_assign_serial.forEach((serial) => {
        formData.append(`new_assign_serial[${product.order_prod_id}][]`, serial);
      });
      product.editable_fees?.forEach((fee) => {
        formData.append(`new_prod_addnl_fee_editable[${product.order_prod_id}][${fee.id}]`, fee.value);
      });
      if (product.custom_order_rental_rates?.length) {
        formData.append(
          `custom_order_rental_rates[${product.order_prod_id}]`,
          JSON.stringify(product.custom_order_rental_rates),
        );
      }
    });

    editable_additional_fees?.forEach((fee) => {
      formData.append(`editable_additional_fee_${fee.id}`, fee.value);
    });

    const config = {
      method: 'post',
      url: `${contextPath}manager/orders/getOrderTotals`,
      data: formData,
      responseType: 'text',
      transformResponse: (data) => {
        return JSON.parse(data);
      },
      headers: {
        'X-Tenant': tenant,
        'Legacy-Route': true,
        'Content-Type': 'multipart/form-data',
      },
    } as AxiosRequestConfig;

    return await handleRequest<OrderTotalsLegacyResponse>(config);
  }

  async function assignUnitFromQRCodeLegacy(
    orderId: number,
    qrCode: string,
  ): Promise<{
    status: number;
    msg: string;
    addHourReading: number;
    productId: number;
    unitId: number;
    lastHourReading: number;
    label: string;
  }> {
    const formData = new FormData();
    formData.append('order_id', orderId.toString());
    formData.append('qrCode', qrCode);
    formData.append('order_status', '1');
    formData.append('mode', 'ajax');

    const config = {
      method: 'post',
      url: `${contextPath}legacy-app/manager/orders/assignUnitFromQRCode`,
      data: formData,
      responseType: 'text',
      transformResponse: (data) => {
        return JSON.parse(data);
      },
      headers: {
        'X-Tenant': tenant,
        'Legacy-Route': true,
        'Content-Type': 'multipart/form-data',
      },
    } as AxiosRequestConfig;

    return await handleRequest<{
      status: number;
      msg: string;
      addHourReading: number;
      productId: number;
      unitId: number;
      lastHourReading: number;
      label: string;
    }>(config);
  }

  async function addHourReadingFromQRLegacy(
    orderId: number,
    productId: number,
    unitId: number,
    hour: number,
  ): Promise<{ status: number; msg: string }> {
    const formData = new FormData();
    formData.append('orderId', orderId.toString());
    formData.append('productId', productId.toString());

    formData.append('unitId', unitId.toString());
    formData.append('hour', hour.toString());
    formData.append('reload', '1');

    const config = {
      method: 'post',
      url: `${contextPath}legacy-app/manager/orders/addHourReadingFromQR`,
      data: formData,
      responseType: 'text',
      transformResponse: (data) => {
        return JSON.parse(data);
      },
      headers: {
        'X-Tenant': tenant,
        'Legacy-Route': true,
        'Content-Type': 'multipart/form-data',
      },
    } as AxiosRequestConfig;

    return await handleRequest<{ status: number; msg: string }>(config);
  }

  async function getProductInfoLegacy(request: ProductInfoLegacyRequest): Promise<ProductInfoLegacyResponse> {
    const formData = new FormData();

    formData.append('product_id', request.product_id);
    formData.append('shop_id', request.shop_id);
    formData.append('order_id', request.order_id);
    formData.append('rental_start_date', request.rental_start_date);
    formData.append('rental_start_time', request.rental_start_time);
    formData.append('rental_end_date', request.rental_end_date || '');
    formData.append('rental_end_time', request.rental_end_time || '');
    formData.append('product_for', request.product_for);
    formData.append('custom_order_rental_rates', JSON.stringify(request.custom_order_rental_rates));

    const config = {
      method: 'post',
      url: `${contextPath}manager/orders/submit_product_form`,
      data: formData,
      responseType: 'text',
      transformResponse: (data: string) => {
        return JSON.parse(data)?.result;
      },
      headers: {
        'X-Tenant': tenant,
        'Legacy-Route': true,
        'Content-Type': 'multipart/form-data',
      },
    } as AxiosRequestConfig;

    return await handleRequest<ProductInfoLegacyResponse>(config);
  }

  async function submitEditOrderLegacy(request: OrderTotalsLegacyRequest, isV2: boolean = false) {
    const formData = new FormData();
    const { products, editable_additional_fees, ...props } = request;

    formData.append('isV2', isV2 ? '1' : '0');

    Object.entries(props).forEach(([key, value]) => {
      formData.append(key, value);
    });

    products.forEach((product) => {
      formData.append('new_product_id[]', product.new_product_id);
      formData.append('order_prod_id[]', product.order_prod_id);
      formData.append('new_product_start[]', product.new_product_start);
      formData.append('new_product_end[]', product.new_product_end);
      formData.append('new_product_type[]', product.new_product_type);
      formData.append('new_prod_unique_id[]', product.new_prod_unique_id);
      formData.append('new_product_delivery_method[]', product.new_product_delivery_method);
      formData.append('new_prod_editable_price[]', product.new_prod_editable_price);
      formData.append('new_prod_editable_disc[]', product.new_prod_editable_disc);
      formData.append('new_product_qty[]', product.new_product_qty);
      product.new_assign_serial.forEach((serial) => {
        formData.append(`new_assign_serial[${product.order_prod_id}][]`, serial);
      });
      product.editable_fees?.forEach((fee) => {
        formData.append(`new_prod_addnl_fee_editable[${product.order_prod_id}][${fee.id}]`, fee.value);
      });
      if (product.custom_order_rental_rates?.length) {
        formData.append(
          `custom_order_rental_rates[${product.order_prod_id}]`,
          JSON.stringify(product.custom_order_rental_rates),
        );
      }
    });

    editable_additional_fees?.forEach((fee) => {
      formData.append(`editable_additional_fee_${fee.id}`, fee.value);
    });

    const config = {
      method: 'post',
      url: `${contextPath}legacy-app/manager/orders/submiteditorder`,
      data: formData,
      transformResponse: !isV2
        ? (data: string) => {
            const htmlResponse = $.parseHTML(data);
            const systemMessage = $(htmlResponse).filter('.system_message');
            return systemMessage.html() || '';
          }
        : undefined,
      headers: {
        'X-Tenant': tenant,
        'Legacy-Route': true,
        'Content-Type': 'multipart/form-data',
      },
    };

    return await handleRequest<{ status: number; msg: string } | string>(config);
  }

  async function updateDeliveryCostLegacy(
    sourceAddress: string,
    destinationAddress: string,
  ): Promise<{ status: number; message: string }> {
    const formData = new FormData();
    const request = JSON.stringify({ status: 1, source: sourceAddress, destination: destinationAddress });
    formData.append('data', JSON.stringify(request));

    const config = {
      method: 'post',
      url: `${contextPath}legacy-app/manager/orders/updateDeliveryCost`,
      data: formData,
      responseType: 'text',
      transformResponse: (data: string) => {
        return JSON.parse(data);
      },
      headers: {
        'X-Tenant': tenant,
        'Legacy-Route': true,
        'Content-Type': 'multipart/form-data',
      },
    } as AxiosRequestConfig;

    return await handleRequest<any>(config);
  }

  function submitBufferPeriod(request: OrderBufferPeriodLegacyRequest) {
    const form = new FormData();
    form.append('order_id', `${request.order_id}`);

    request.order_product_buffer.forEach((buffer) => form.append('order_product_buffer[]', JSON.stringify(buffer)));

    const config = {
      method: 'post',
      url: `${contextPath}manager/orders/submit_buffer_period_form`,
      headers: {
        'X-Tenant': tenant,
        'Legacy-Route': true,
      },
      data: form,
    };
    return handleRequest<{ status: number; msg: string; opr_id?: number }>(config);
  }

  return {
    getOffRentForm,
    getOffRentWithInventoryForm,
    getOnRentForm,
    getInventoryReturnForm,
    resendCustomerReviewEmail,
    getNewProductPrice,
    getOrderTotalsLegacy,
    assignUnitFromQRCodeLegacy,
    addHourReadingFromQRLegacy,
    getProductInfoLegacy,
    submitEditOrderLegacy,
    updateDeliveryCostLegacy,
    submitBufferPeriod,
  };
};

async function handleRequest<T>(req: AxiosRequestConfig): Promise<T> {
  const response = await axios(req);
  return response.data as T;
}
